import type {
  IResponseClip,
  IResponseEvent,
  IResponseHighlight,
  IResponseMBEEvent,
  IResponsePlayerHighlight,
  IVideoAngles,
  TBasicVideoAngles,
  TClippedVideoAngle,
  TContinuousVideoAngle,
} from "~/types";
import type { IPixellotPlayerSourceType } from "~~/modules/pixellot-sdk";
import { VIDEO_SOURCE_TYPE } from "~/types";

function formatEventVideoAnglesMap(data: TBasicVideoAngles): IVideoAngles {
  const continuous = new Map<IPixellotPlayerSourceType, TContinuousVideoAngle>();
  const clipped = new Map<IPixellotPlayerSourceType, TClippedVideoAngle>();

  for (const item of data) {
    const key = item.type as IPixellotPlayerSourceType;

    if (item.videoType === VIDEO_SOURCE_TYPE.CLIPPED) {
      clipped.set(key, item as TClippedVideoAngle);
    } else {
      continuous.set(key, item as TContinuousVideoAngle);
    }
  }

  return { continuous, clipped };
}

function formatContinuousVideoAngles(videos: IResponseEvent["attributes"]["videosList"], urls?: IResponseEvent["attributes"]["urls"]): TContinuousVideoAngle[] {
  if (!videos.length) return [];

  const angles: TContinuousVideoAngle[] = [];

  for (const angle of videos) {
    const videoAngleItem: TContinuousVideoAngle = {
      url: angle.url,
      type: angle.type,
      thumbnails_url: angle.previewThumbnail,
      duration: angle.duration,
      mp4_url: angle.downloadURL,
      videoType: angle.videoType,
      primary: angle.primary ?? false,
    };

    if (videoAngleItem.type === "hd" && urls?.hdMP4) {
      videoAngleItem.mp4_url = urls.hdMP4;
    } else if (videoAngleItem.type === "pano" && urls?.panoMP4) {
      videoAngleItem.mp4_url = urls.panoMP4;
    }

    if (videoAngleItem.url) {
      angles.push(videoAngleItem);
    }
  }

  return angles;
}

function formatClippedVideoAngles(videos: IResponseEvent["attributes"]["videosList"]): TClippedVideoAngle[] {
  if (!videos.length) return [];

  const angles: TClippedVideoAngle[] = [];

  for (const angle of videos) {
    const clippedAngleItem: TClippedVideoAngle = {
      type: angle.type,
      videoType: angle.videoType,
      primary: angle.primary ?? false,
      videos:
        angle.videos?.map((video) => ({
          url: video.url,
          id: video._id,
          type: angle.type,
          thumbnails_url: video.previewThumbnail,
          mp4_url: video.downloadURL,
          duration: video.duration,
          index: video.index,
        })) ?? [],
    };

    if (clippedAngleItem.videos?.length) {
      angles.push(clippedAngleItem);
    }
  }

  return angles;
}

export function formatEventVideoAngles(data: IResponseEvent): IVideoAngles {
  if (data.attributes.videosList?.length) {
    // Separate clipped and continuous videos
    const continuousVideos = [];
    const clippedVideos = [];

    for (const video of data.attributes.videosList) {
      if (video.videoType === VIDEO_SOURCE_TYPE.CLIPPED) {
        clippedVideos.push(video);
      } else {
        continuousVideos.push(video);
      }
    }

    const angles: TBasicVideoAngles = [...formatContinuousVideoAngles(continuousVideos, data.attributes.urls), ...formatClippedVideoAngles(clippedVideos)];

    return formatEventVideoAnglesMap(angles);
  }

  const angles = [
    {
      url: data.attributes.urls?.hd,
      type: "hd" as const,
      thumbnails_url: data.attributes.thumbnails.hd,
    },
    {
      url: data.attributes.urls?.pano,
      type: "pano" as const,
      thumbnails_url: data.attributes.previewThumbnails.pano,
    },
  ].filter((src) => src.url) as TBasicVideoAngles;

  return formatEventVideoAnglesMap(angles);
}

export function formatMBEEventVideoAngles(data: IResponseMBEEvent): IVideoAngles {
  const angles = [
    {
      url: data.attributes.urls?.hd,
      type: "hd" as const,
      thumbnails_url: data.attributes.urls?.hdThumbnailUrl,
    },
    {
      url: data.attributes.urls?.pano,
      type: "pano" as const,
      thumbnails_url: data.attributes.urls?.panoThumbnailUrl,
    },
  ].filter((src) => src.url) as TBasicVideoAngles;

  return formatEventVideoAnglesMap(angles);
}

export function formatClipVideoAngles(data: IResponseClip): IVideoAngles {
  const angles: TBasicVideoAngles = [
    {
      url: data.attributes.urls.clip as string,
      type: "hd" as const,
      mp4_url: data.attributes.downloadUrls.clip,
    },
  ].filter((src) => src.url);
  return formatEventVideoAnglesMap(angles);
}

export function formatHighlightVideoAngles(data: IResponseHighlight): IVideoAngles {
  const angles: TBasicVideoAngles = [
    {
      url: data.attributes.urls.highlight,
      type: "hd" as const,
      mp4_url: data.attributes.downloadUrls.highlight,
    },
  ].filter((src) => src.url);
  return formatEventVideoAnglesMap(angles);
}

export function formatInlineHighlightVideoAngles(data: IResponseEvent["attributes"]["highlights"][0]): IVideoAngles {
  const angles: TBasicVideoAngles = [
    {
      url: data.url,
      type: "hd" as const,
      mp4_url: data.downloadUrl,
    },
  ].filter((src) => src.url);
  return formatEventVideoAnglesMap(angles);
}

export function formatPlayerHighlightVideoAngles(data: IResponsePlayerHighlight): IVideoAngles {
  const angles: TBasicVideoAngles = [
    {
      url: data.attributes.urls.highlight,
      type: "hd" as const,
      mp4_url: data.attributes.downloadUrls.highlight,
      thumbnails_url: null,
    },
  ].filter((src) => src.url);
  return formatEventVideoAnglesMap(angles);
}
